export function productLines(cart, productId) {
  return cart.items.filter(item => String(item.product_id) === String(productId));
}

export function nextQuantity(line, direction) {
  const step = line.quantity_rule?.increment || 1;
  const minimum = line.quantity_rule?.min || step;
  const quantity = line.quantity + direction * step;
  return direction < 0 && quantity < minimum ? 0 : quantity;
}

export function createCartClient({ root, request = fetch, onChange = () => {}, errorMessage, staleMessage }) {
  let queue = Promise.resolve();
  async function json(path, options = {}) {
    const response = await request(`${root}${path}`, { credentials: 'same-origin', cache: 'no-store', ...options });
    const data = await response.json();
    if (!response.ok) throw new Error(typeof data.description === 'string' ? data.description : errorMessage);
    return data;
  }
  async function read() {
    const cart = await json('cart.js');
    onChange(cart);
    return cart;
  }
  function serialize(action) {
    const result = queue.then(action);
    queue = result.catch(() => {});
    return result;
  }
  async function mutate(action) {
    try {
      await action(await read());
    } catch (error) {
      await read().catch(() => {});
      throw error;
    }
    return read();
  }
  return {
    refresh: () => serialize(read),
    add: body => serialize(() => mutate(() => json('cart/add.js', { method: 'POST', body }))),
    change: (key, direction) => serialize(() => mutate(cart => {
      const line = cart.items.find(item => item.key === key);
      if (!line) throw new Error(staleMessage);
      return json('cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: line.key, quantity: nextQuantity(line, direction) }),
      });
    })),
  };
}

if (typeof document !== 'undefined' && !customElements.get('asap-inline-cart')) {
  const components = new Set();
  let cart;
  let busy = false;
  let initialRead;
  let client;

  function publish(value) {
    cart = value;
    components.forEach(component => component.render());
    window.dispatchEvent(new CustomEvent('cartcount:update', { detail: cart }));
  }

  function refresh() {
    return client?.refresh().catch(() => {});
  }

  async function perform(component, action) {
    if (busy) return;
    const active = component.contains(document.activeElement) ? document.activeElement : null;
    const activeKey = active?.dataset.cartKey;
    const activeDirection = active?.dataset.cartDirection;
    busy = true;
    components.forEach(control => control.setBusy(true));
    component.message(component.dataset.loading);
    try {
      await action();
      component.message(component.dataset.updated);
    } catch (error) {
      component.message(error instanceof TypeError ? component.dataset.error : error.message || component.dataset.error, true);
    } finally {
      busy = false;
      components.forEach(control => control.setBusy(false));
      if (active && (document.activeElement === document.body || document.activeElement === active)) {
        const buttons = [...component.querySelectorAll('[data-cart-direction]')];
        const target = buttons.find(button => button.dataset.cartKey === activeKey && button.dataset.cartDirection === activeDirection)
          || buttons[0] || component.querySelector('[data-asap-card-form] button') || component.closest('form')?.querySelector('[type="submit"]');
        target?.focus({ preventScroll: true });
      }
    }
  }

  class InlineCart extends HTMLElement {
    connectedCallback() {
      if (components.has(this)) return;
      components.add(this);
      if (!client) {
        client = createCartClient({
          root: window.Shopify.routes.root,
          onChange: publish,
          errorMessage: this.dataset.error,
          staleMessage: this.dataset.stale,
        });
      }
      this.render();
      this.setBusy(busy);
      initialRead ||= client.refresh().catch(() => {});
    }

    disconnectedCallback() { components.delete(this); }

    message(text, error = false) {
      const status = this.querySelector('[data-cart-status]');
      status.textContent = text;
      status.toggleAttribute('data-error', error);
    }

    setBusy(value) {
      this.setAttribute('aria-busy', String(value));
      this.querySelectorAll('[data-cart-direction], [data-asap-card-form] button').forEach(button => { button.disabled = value; });
    }

    render() {
      if (!cart) return;
      const lines = productLines(cart, this.dataset.productId);
      const quantity = lines.reduce((sum, line) => sum + line.quantity, 0);
      const contents = this.querySelector('[data-cart-contents]');
      contents.hidden = !quantity;
      this.querySelector('[data-cart-summary]').textContent = `${this.dataset.inCart}: ${quantity}`;
      const container = this.querySelector('[data-cart-lines]');
      container.replaceChildren();
      lines.forEach(line => {
        const row = document.createElement('div');
        row.className = 'asap-inline-cart__line';
        const details = [];
        if (line.variant_title && line.variant_title !== 'Default Title') details.push(line.variant_title);
        if (line.selling_plan_allocation) details.push(line.selling_plan_allocation.selling_plan.name);
        Object.entries(line.properties || {}).forEach(([key, value]) => {
          if (!key.startsWith('_') && value) details.push(`${key}: ${value}`);
        });
        if (details.length) {
          const label = document.createElement('p');
          label.className = 'asap-inline-cart__label';
          label.textContent = details.join(' · ');
          row.append(label);
        }
        const stepper = document.createElement('div');
        stepper.className = 'asap-inline-cart__stepper';
        const name = [this.dataset.productTitle, ...details].join(' · ');
        [-1, 1].forEach(direction => {
          if (direction === 1) {
            const output = document.createElement('output');
            output.textContent = line.quantity;
            output.setAttribute('aria-label', `${this.dataset.inCart}: ${name}`);
            stepper.append(output);
          }
          const button = document.createElement('button');
          button.type = 'button';
          button.textContent = direction === 1 ? '+' : '−';
          button.dataset.cartKey = line.key;
          button.dataset.cartDirection = direction;
          button.setAttribute('aria-label', `${direction === 1 ? this.dataset.increase : this.dataset.decrease}: ${name}`);
          button.disabled = busy;
          stepper.append(button);
        });
        row.append(stepper);
        container.append(row);
      });
      const cardForm = this.querySelector('[data-asap-card-form]');
      if (cardForm) cardForm.hidden = quantity > 0;
    }
  }

  customElements.define('asap-inline-cart', InlineCart);

  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const component = form.querySelector('asap-inline-cart') || form.closest('asap-inline-cart');
    if (!component) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (busy) return;
    const body = new FormData(form);
    if (!/^\d+$/.test(body.get('id') || '')) {
      component.message(component.dataset.selectVariant, true);
      return;
    }
    if (!body.has('quantity')) body.set('quantity', '1');
    perform(component, () => client.add(body));
  }, true);

  document.addEventListener('click', event => {
    const button = event.target.closest('[data-cart-direction]');
    if (!button) return;
    const component = button.closest('asap-inline-cart');
    if (!component) return;
    event.preventDefault();
    perform(component, () => client.change(button.dataset.cartKey, Number(button.dataset.cartDirection)));
  });

  window.addEventListener('pageshow', refresh);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) refresh(); });
  document.addEventListener('addToCartFlyout:complete', refresh);
  document.addEventListener('product-form:cart:update', refresh);
}
